/**
 * FriendsList component
 * Displays the list of friends using a unified FlashList
 * Receives processed items and passes them to FriendsGrid
 */

import React, { useMemo, useCallback } from 'react'
import { View, Text, Pressable } from 'react-native'
import { useRouter } from 'expo-router'
import { Users } from 'lucide-react-native'
import { getFriendsListStyles } from './FriendsList.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useFriendsPage } from '../../../src/friends/contexts/FriendsPageContext'
import { FriendCard } from '../FriendCard/FriendCard'
import { GroupCard } from '../GroupCard'
import { FriendsGrid } from './FriendsGrid'
import { GroupPendingCard } from './GroupPendingCard'
import type { FriendListItem } from '../../../src/friends/hooks/useFriendData'
import type { GroupListItem } from '../../../src/friends/hooks/useGroupsData'
import { VIEW_TYPE, type ViewType, type FilterType } from '../../../src/friends/constants/viewFilter.constants'
import {
  filterUnifiedListItems,
  EMPTY_STATE_COPY,
  INVITE_BUTTON_ITEM,
  CREATE_GROUP_BUTTON_ITEM,
  type UnifiedListItem,
  type FriendsGridItem,
} from '../../../src/friends/helpers/unifiedList.helper'
import { InviteCard } from '../InviteCard/InviteCard'
import { AddGroupCard } from '../AddGroupCard/AddGroupCard'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useDialog } from '../../../src/shared/hooks/useDialog'
import { GROUP_LEADERBOARD_PATH } from '../../../src/friends/constants/groups.constants'
import { useQueryClient } from '@tanstack/react-query'
import { useGridDimensions } from '../../../src/shared/hooks/useGridDimensions'
import { useGroupCardActions } from '../../../src/friends/hooks/useGroupCardActions'
import { usePlayerRanks } from '../../../src/economy/hooks/usePlayerRanks'

interface FriendsListProps {
  items: UnifiedListItem[]
  screenWidth?: number
  /** When set, grid uses this width (e.g. screen minus horizontal padding) so cards are centered. */
  contentWidth?: number
  filterType: FilterType // Now controlled from parent
  viewType: ViewType // Friends or Groups
  /** Called when user taps the Create group card (Groups view). */
  onRequestCreateGroup?: () => void
  /** Opens invite flow or guest sign-up prompt (parent owns modal vs requireAuth). */
  onInviteFriendsPress: () => void
  /** Test ID for testing */
  testID?: string
}

export const FriendsList: React.FC<FriendsListProps> = ({
  items,
  screenWidth: propScreenWidth,
  contentWidth: propContentWidth,
  filterType,
  viewType,
  onRequestCreateGroup,
  onInviteFriendsPress,
  testID,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const styles = useMemo(() => getFriendsListStyles(theme), [theme])
  const { userId: currentUserId } = useSupabaseAuth()

  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { showDialog, showAlert } = useDialog()
  const {
    handleAcceptInvitation,
    handleDeclineInvitation,
    handleRemoveFriend,
    actionLoadingId,
    refreshGroups,
  } = useFriendsPage()

  const {
    handleRemoveGroup,
    handleAcceptGroupInvitation,
    groupActionLoadingId,
  } = useGroupCardActions({
    apiClient,
    queryClient,
    showDialog,
    showAlert,
    refreshGroups,
  })

  const { numColumns, screenWidth } = useGridDimensions({
    screenWidth: propScreenWidth,
  })

  const { filteredItems, counts } = useMemo(
    () => filterUnifiedListItems(items, filterType, viewType),
    [items, filterType, viewType]
  )

  // Rank/level for the friends on screen, in one batch (specs/rank-on-social-screens AC-15).
  // Owned here rather than per-card: a card-level hook would be one request per card.
  const friendIds = useMemo(
    () =>
      filteredItems
        .filter((i): i is FriendListItem => 'friend' in i)
        .map((i) => i.friend.id),
    [filteredItems]
  )
  const { getPlayerRank } = usePlayerRanks(friendIds)

  // Calculate pending request count (only for received invitations in Friends view)
  const pendingRequestCount = useMemo(() => {
    if (viewType !== 'FRIENDS') return 0
    return filteredItems.filter(
      (item): item is FriendListItem =>
        'friend' in item &&
        item.friend.friendshipStatus === 'pending' &&
        item.friend.invitationType === 'received'
    ).length
  }, [filteredItems, viewType])

  const renderFriendCard = useCallback(
    (item: FriendListItem, cardWidth: number) => {
      const friend = item.friend
      let requestIndex = 0
      if (friend.friendshipStatus === 'pending' && friend.invitationType === 'received') {
        const receivedInvitations = filteredItems.filter(
          (i): i is FriendListItem =>
            'friend' in i &&
            i.friend.friendshipStatus === 'pending' &&
            i.friend.invitationType === 'received'
        )
        requestIndex = receivedInvitations.findIndex((i) => i.friend.id === friend.id)
      }
      return (
        <FriendCard
          friend={friend}
          cardWidth={cardWidth}
          playerRank={getPlayerRank(friend.id)}
          onRemoveFriend={
            friend.friendshipStatus === 'pending' ? handleRemoveFriend : undefined
          }
          onAcceptInvitation={handleAcceptInvitation}
          onDeclineInvitation={handleDeclineInvitation}
          actionLoadingId={actionLoadingId}
          pendingRequestCount={pendingRequestCount}
          requestIndex={requestIndex}
        />
      )
    },
    [
      filteredItems,
      pendingRequestCount,
      handleRemoveFriend,
      handleAcceptInvitation,
      handleDeclineInvitation,
      actionLoadingId,
      getPlayerRank,
    ]
  )

  const renderGroupCard = useCallback(
    (item: GroupListItem, cardWidth: number) => (
      <GroupCard
        group={item.group}
        isCreator={currentUserId != null && item.group.creatorId === currentUserId}
        myPosition={item.group.myPosition}
        cardWidth={cardWidth}
        onPress={() => router.push(`${GROUP_LEADERBOARD_PATH}/${item.group.id}`)}
      />
    ),
    [currentUserId, router]
  )

  const renderGroupPendingCard = useCallback(
    (item: GroupListItem, _cardWidth: number) => (
      <GroupPendingCard
        group={item.group}
        onAccept={handleAcceptGroupInvitation}
        onDecline={handleRemoveGroup}
        onRemove={handleRemoveGroup}
        isLoading={groupActionLoadingId === item.group.invitationId}
        disabled={!!groupActionLoadingId}
      />
    ),
    [handleRemoveGroup, handleAcceptGroupInvitation, groupActionLoadingId]
  )

  const handleInvitePress = useCallback(() => {
    onInviteFriendsPress()
  }, [onInviteFriendsPress])

  const handleCreateGroupPress = useCallback(() => {
    onRequestCreateGroup?.()
  }, [onRequestCreateGroup])

  const renderCard = useCallback(
    ({
      item,
      cardWidth,
    }: {
      item: FriendsGridItem
      index: number
      cardWidth: number
    }) => {
      if (item.type === 'INVITE') {
        return (
          <InviteCard
            cardWidth={cardWidth}
            onPress={handleInvitePress}
            testID="invite-card"
          />
        )
      }
      if (item.type === 'CREATE_GROUP') {
        return (
          <AddGroupCard
            cardWidth={cardWidth}
            onPress={handleCreateGroupPress}
            testID="add-group-card"
          />
        )
      }
      switch (item.type) {
        case 'REQUEST':
        case 'FRIEND':
        case 'PENDING':
          return renderFriendCard(item, cardWidth)
        case 'GROUP_ACTIVE':
          return renderGroupCard(item, cardWidth)
        case 'GROUP_PENDING':
          return renderGroupPendingCard(item, cardWidth)
        default:
          return null
      }
    },
    [
      renderFriendCard,
      renderGroupCard,
      renderGroupPendingCard,
      handleInvitePress,
      handleCreateGroupPress,
    ]
  )

  const emptyCopy = EMPTY_STATE_COPY[viewType]


  // When we have no items, show empty state only for Friends view. For Groups view, show the grid
  // with just the large Create card (same pattern as Invite card on Friends) so the Create button is always visible.
  const hasNoItems = items.length === 0
  const showEmptyStateOnly = hasNoItems && viewType === VIEW_TYPE.FRIENDS

  if (showEmptyStateOnly) {
    return (
      <View style={styles.container} testID={testID || 'friends-list-empty'}>
        <View style={styles.emptyContainer}>
          <Users
            size={64}
            color={theme.primaryLight ?? theme.gray[400]}
            style={styles.emptyIcon}
          />
          <Text style={styles.emptyTitle}>No {emptyCopy.title} yet</Text>
          <Text style={styles.emptyText}>{emptyCopy.subtitle}</Text>
          <Pressable
            style={({ pressed }) => [styles.emptyInviteButton, pressed && { opacity: 0.8 }]}
            onPress={onInviteFriendsPress}
          >
            <Text style={styles.emptyInviteButtonText}>Invite Friends</Text>
          </Pressable>
        </View>
      </View>
    )
  }

  // Show empty state if Active tab is selected but has no items. In Groups view always show the grid (with Create card) so the large Create button is visible.
  const showEmptyState =
    viewType === VIEW_TYPE.FRIENDS &&
    filterType === 'FRIEND' &&
    counts.friend === 0 &&
    filteredItems.length === 0

  return (
    <View style={styles.container} testID={testID || 'friends-list'}>
      {showEmptyState ? (
        <View style={styles.emptyContainer}>
          <Users
            size={64}
            color={theme.primaryLight ?? theme.gray[400]}
            style={styles.emptyIcon}
          />
          <Text style={styles.emptyTitle}>No {emptyCopy.title} yet</Text>
          <Text style={styles.emptyText}>{emptyCopy.subtitle}</Text>
          <Pressable
            style={({ pressed }) => [styles.emptyInviteButton, pressed && { opacity: 0.8 }]}
            onPress={onInviteFriendsPress}
          >
            <Text style={styles.emptyInviteButtonText}>Invite Friends</Text>
          </Pressable>
        </View>
      ) : (
        <FriendsGrid
          items={
            viewType === VIEW_TYPE.FRIENDS
              ? [...filteredItems, INVITE_BUTTON_ITEM]
              : [...filteredItems, CREATE_GROUP_BUTTON_ITEM]
          }
          renderCard={renderCard}
          numColumns={numColumns}
          screenWidth={screenWidth}
          contentWidth={propContentWidth}
        />
      )}
    </View>
  )
}

